/**
 * Notification Logs Component
 * Display push notification logs grouped by batch with expandable recipient details
 */

import { useState } from 'react';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { usePushLogs } from '../hooks/usePushNotifications';
import { useI18n } from '@/i18n';
import type { PushLogBatch, PushLogRecipient } from '@/types/push';

export function NotificationLogs() {
  const { messages } = useI18n();
  const t = messages.pushNotifications;
  const tc = messages.common;
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expandedBatchKey, setExpandedBatchKey] = useState<string | null>(null);
  const limit = 20;

  const { data, isLoading, error, refetch } = usePushLogs({
    status_filter: statusFilter || undefined,
    page,
    limit,
    grouped: true,
  });

  const logs = (data?.logs || []) as PushLogBatch[];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t.time.justNow;
    if (diffMins < 60) return t.time.minutesAgo.replace('{n}', String(diffMins));
    if (diffHours < 24) return t.time.hoursAgo.replace('{n}', String(diffHours));
    if (diffDays < 7) return t.time.daysAgo.replace('{n}', String(diffDays));

    return new Intl.DateTimeFormat('zh-CN', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatFullDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const formatDataPayload = (data: Record<string, unknown> | null | undefined) => {
    if (!data || Object.keys(data).length === 0) return null;
    return JSON.stringify(data, null, 2);
  };

  const getActionLabel = (data: Record<string, unknown> | null | undefined) => {
    if (!data) return null;
    if (data.type === 'navigation') return t.logs?.actionNavigation ?? '导航';
    if (data.type === 'action') return t.logs?.actionType ?? '动作';
    if (data.type === 'fortune') return t.logs?.actionFortune ?? '运势';
    return null;
  };

  const toggleExpand = (batchKey: string) => {
    setExpandedBatchKey(expandedBatchKey === batchKey ? null : batchKey);
  };

  const renderRecipient = (recipient: PushLogRecipient) => {
    const isSuccess = recipient.status === 'sent';
    return (
      <div
        key={recipient.id}
        className={`flex items-center justify-between p-2 rounded text-sm ${
          isSuccess ? 'bg-green-50' : 'bg-red-50'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="flex-shrink-0">{isSuccess ? '✅' : '❌'}</span>
          <span className="font-medium truncate">{recipient.display_name}</span>
          {recipient.platform && (
            <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
              recipient.platform === 'ios'
                ? 'bg-gray-200 text-gray-700'
                : 'bg-green-100 text-green-700'
            }`}>
              {recipient.platform === 'ios' ? 'iOS' : 'Android'}
            </span>
          )}
          {recipient.device_info && Object.keys(recipient.device_info).length > 0 && (
            <span className="text-xs text-gray-500 truncate">
              {recipient.device_info.model ? String(recipient.device_info.model) : null}
            </span>
          )}
        </div>
        {recipient.error_message && (
          <span className="text-xs text-red-600 ml-2 truncate max-w-[200px]" title={recipient.error_message}>
            {recipient.error_message}
          </span>
        )}
      </div>
    );
  };

  const renderBatch = (batch: PushLogBatch) => {
    const isExpanded = expandedBatchKey === batch.batch_key;
    const actionLabel = getActionLabel(batch.data);
    const dataPayload = formatDataPayload(batch.data);
    const hasFailures = batch.failed_count > 0;

    return (
      <div
        key={batch.batch_key}
        className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
      >
        {/* Batch header - clickable to expand */}
        <div
          className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleExpand(batch.batch_key)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Title row */}
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-medium text-sm text-gray-900 truncate max-w-[250px]">
                  {batch.title}
                </span>
                {actionLabel && (
                  <span className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">
                    {actionLabel}
                  </span>
                )}
              </div>
              {/* Body */}
              <p className="text-sm text-gray-600 truncate">{batch.body}</p>
              {/* Stats row */}
              <div className="flex items-center gap-3 mt-2 text-xs">
                <span className="text-gray-400" title={formatFullDate(batch.sent_at)}>
                  {formatDate(batch.sent_at)}
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-600">
                  {batch.total_count} {t.logs?.recipients ?? '个接收者'}
                </span>
                <span className="text-green-600">
                  {batch.sent_count} ✓
                </span>
                {hasFailures && (
                  <span className="text-red-600">
                    {batch.failed_count} ✗
                  </span>
                )}
              </div>
            </div>
            {/* Expand indicator */}
            <div className="ml-2 text-gray-400 flex-shrink-0">
              <span className={`transition-transform inline-block ${isExpanded ? 'rotate-90' : ''}`}>
                ▶
              </span>
            </div>
          </div>
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <div className="border-t border-gray-200 bg-gray-50">
            {/* Batch info */}
            <div className="p-3 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">{t.logs?.sentAt ?? '发送时间'}:</span>
                  <span className="ml-2 text-gray-700">{formatFullDate(batch.sent_at)}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t.logs?.totalRecipients ?? '接收者数量'}:</span>
                  <span className="ml-2 text-gray-700">
                    {batch.total_count} ({t.logs?.statusSent ?? '成功'}: {batch.sent_count}, {t.logs?.statusFailed ?? '失败'}: {batch.failed_count})
                  </span>
                </div>
              </div>
              {/* Full content */}
              <div className="mt-3 space-y-2">
                <div>
                  <span className="text-sm text-gray-500">{t.logs?.fullTitle ?? '完整标题'}:</span>
                  <p className="text-sm text-gray-800">{batch.title}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">{t.logs?.fullBody ?? '完整内容'}:</span>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{batch.body}</p>
                </div>
                {dataPayload && (
                  <div>
                    <span className="text-sm text-gray-500">{t.logs?.dataPayload ?? '自定义数据'}:</span>
                    <pre className="mt-1 p-2 bg-gray-100 rounded text-xs font-mono text-gray-700 overflow-x-auto">
                      {dataPayload}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Recipients list */}
            <div className="p-3">
              <div className="text-sm text-gray-500 mb-2">
                {t.logs?.recipientList ?? '接收者列表'}:
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {batch.recipients.map(renderRecipient)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header with filters */}
      <div className="mb-4 flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{t.logs.title}</h4>
        <div className="flex items-center gap-2">
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: t.logs.allStatuses },
              { value: 'sent', label: t.logs.statusSent },
              { value: 'failed', label: t.logs.statusFailed },
            ]}
            className="w-36"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {isLoading ? t.logs.loading : t.logs.refresh}
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="text-red-600 text-sm text-center py-8">
          {tc.error}: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-gray-500 text-sm text-center py-8">
          <div className="text-4xl mb-2">📭</div>
          {t.logs.noLogs}
        </div>
      ) : (
        <>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {logs.map(renderBatch)}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {t.logs.page} {page} {t.logs.of} {totalPages} ({total} {t.logs?.batches ?? '批次'})
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t.logs.previous}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {t.logs.next}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
